"use strict";
/**
 * `ReactionSet` enum, its Zod schema, the reactions each set offers, and the
 * pure helpers translating a space's reactions to and from its feature flags.
 *
 * The set is a first-class field on the space resource, `reactionSet`. It is
 * one of the emoji sets, or `upvote` for an audience-input space, which offers
 * an upvote in place of emoji. Storage keeps the emoji set as one feature key
 * per non-default set, and `upvote` follows from the `audience_input` format
 * flag rather than being stored. The server reads the set with
 * `getCurrentReactionSet` and writes it with `getFeaturesForReactionSet`.
 *
 * Reaction labels are translated strings, so they stay on the client
 * (`client/feature/question/reaction-types.tsx`), keyed by `ReactionKey`.
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.REACTION_SET_REACTIONS = exports.emojiReactionSetSchema = exports.reactionSetSchema = exports.ReactionSet = void 0;
exports.getCurrentReactionSet = getCurrentReactionSet;
exports.getFeaturesForReactionSet = getFeaturesForReactionSet;
const z = __importStar(require("zod"));
var ReactionSet;
(function (ReactionSet) {
    ReactionSet["DEFAULT"] = "default";
    ReactionSet["FUN"] = "fun";
    ReactionSet["PERSONAL"] = "personal";
    ReactionSet["SERIOUS"] = "serious";
    ReactionSet["UPVOTE"] = "upvote";
})(ReactionSet || (exports.ReactionSet = ReactionSet = {}));
exports.reactionSetSchema = z.enum(ReactionSet);
/**
 * The sets a caller can choose. `upvote` isn't one: it follows from a space's
 * format.
 */
exports.emojiReactionSetSchema = z.enum([ReactionSet.DEFAULT, ReactionSet.FUN, ReactionSet.PERSONAL, ReactionSet.SERIOUS]);
exports.REACTION_SET_REACTIONS = {
    [ReactionSet.DEFAULT]: [
        { key: 'Relatable', emoji: '❤️' },
        { key: 'Helpful', emoji: '➕' },
        { key: 'Controversial', emoji: '💬' },
        { key: 'Interesting', emoji: '💡' },
    ],
    [ReactionSet.FUN]: [
        { key: 'Funny', emoji: '😂' },
        { key: 'Enjoyable', emoji: '❤️' },
        { key: 'Surprising', emoji: '🤯' },
        { key: 'Playful', emoji: '😜' },
    ],
    [ReactionSet.PERSONAL]: [
        { key: 'Relatable', emoji: '❤️' },
        { key: 'Helpful', emoji: '➕' },
        { key: 'Interesting', emoji: '💡' },
        { key: 'Nuanced', emoji: '⚖️' },
    ],
    [ReactionSet.SERIOUS]: [
        { key: 'Constructive', emoji: '🤝' },
        { key: 'Insightful', emoji: '💡' },
        { key: 'Empathetic', emoji: '🫴' },
        { key: 'Nuanced', emoji: '⚖️' },
    ],
};
/**
 * The feature key that stores each non-default set. The default set has no
 * key: it is what a space shows with all three off.
 *
 * In precedence order: if an instance somehow has two on, the earlier one
 * wins, matching the emoji the retired per-set client features showed.
 */
const REACTION_SET_FEATURE_KEYS = [
    [ReactionSet.FUN, 'funreactions'],
    [ReactionSet.PERSONAL, 'personalreactions'],
    [ReactionSet.SERIOUS, 'seriousreactions'],
];
/**
 * Reads the space's reaction set out of an instance's feature flags: `upvote`
 * for an audience-input space, otherwise the stored emoji set. A space with no
 * set stored, including one with the legacy `newpublicreactions` or
 * `defaultreactions` keys, reads as the default set.
 */
function getCurrentReactionSet(instanceFeatures) {
    if (instanceFeatures.audience_input)
        return ReactionSet.UPVOTE;
    return REACTION_SET_FEATURE_KEYS.find(([_, featureKey]) => instanceFeatures[featureKey])?.[0] ?? ReactionSet.DEFAULT;
}
/**
 * The feature patch that stores `reactionSet` as the instance's emoji set: its
 * key on and the other set keys off. On an audience-input space the set is
 * kept but not shown, and takes effect if the space changes format.
 */
function getFeaturesForReactionSet(reactionSet) {
    return Object.fromEntries(REACTION_SET_FEATURE_KEYS.map(([set, featureKey]) => [featureKey, set === reactionSet]));
}
//# sourceMappingURL=reaction-sets.js.map